import { useEffect, useMemo } from "react";
import Table, { TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import SearchInput from "@/components/SearchInput";
import usePagination from "@/hooks/usePagination";
import usePageTitle from "@/hooks/usePageTitle";
import TabLayout from "../_TabLayout";
import { Path, useNavigate } from "@/router";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { GET_ACTIVITY_TYPES, ActivityTypesQueryData } from "./queries";
import { useQuery } from "@apollo/client";
import { ActivityType, ActivityTypeTableRow } from "./types";
import { renderCreditRate } from "./_utils";
import { getColumns } from "./TableColumns";

const formatTableData = (activityTypes: ActivityType[]): ActivityTypeTableRow[] => {
  return activityTypes.map((activityType) => {
    return {
      ...activityType,
      activityType: activityType.title,
      creditRate: activityType.config ? renderCreditRate(activityType.config.creditRates) : null,
      publishedActivities: activityType.activities?.totalCount ?? 0
    };
  });
};
export default function ActivityTypesTabPage() {
  const navigate = useNavigate();
  usePageTitle("/activities/activity-types", "Activities | Activity Types");

  const { createHeaderPagination, createFooterPagination, resetPagination, paginationVariables } = usePagination();
  const { Menu, setMenu } = useTableRowMenu();

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm",
    debounceMs: 300
  });

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, resetPagination]);

  const { data, loading } = useQuery<ActivityTypesQueryData>(GET_ACTIVITY_TYPES, {
    variables: {
      filters: {
        search: debouncedSearch || undefined
      },
      ...paginationVariables
    }
  });

  const activityTypes = data?.activityTypes;
  const tableData = formatTableData(activityTypes?.edges?.map((edge) => edge.node) || []);

  const columns = useMemo(
    () =>
      getColumns({
        navigate,
        setMenu
      }),
    [navigate, setMenu]
  );

  return (
    <TabLayout>
      <Menu />
      <div className="pt-6" />
      <Table
        data={tableData}
        loading={loading}
        columns={columns}
        onClickRow={(row) => {
          const path = `/activities/activity-types/${row.original.id}`;
          navigate(path as Path);
        }}
        data-testid="activities-table"
        hasActiveFilters={!!debouncedSearch}
        renderFilteredEmptyState={() => (
          <EmptyState
            title="No Activities Found."
            caption={debouncedSearch ? "Try a different search" : ""}
            iconName="event"
          />
        )}
        renderNoDataEmptyState={() => <EmptyState title="No Activities Found." iconName="event" />}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls pagination={createHeaderPagination(activityTypes, { loading })} onSortClick={onSortClick}>
            <SearchInput
              placeholder="Search Activity Types"
              query={searchValue}
              onClear={() => clearSearch()}
              onChange={(e) => setSearchValue(e)}
            />
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(activityTypes, { loading })}
            totalRowCount={activityTypes?.totalCount ?? tableData.length}
          />
        )}
      />
    </TabLayout>
  );
}
